import { useMemo } from 'react';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { DsMode, Frame } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import * as S from './styles';

interface FrameModeDropdownProps {
	frame: Frame;
	dsModes: DsMode[] | undefined;
	compact?: boolean;
}

const FrameModeDropdown = ({ frame, dsModes, compact = false }: FrameModeDropdownProps) => {
	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------
	const defaultModeId = useMemo(() => {
		if (!dsModes || dsModes.length === 0) return null;
		return dsModes.find((m) => m.default)?.id ?? dsModes[0].id;
	}, [dsModes]);

	const storedMode = frame.metadata?.mode ?? null;
	const activeModeId =
		storedMode && dsModes?.some((m) => m.id === storedMode) ? storedMode : defaultModeId;
	const activeMode = dsModes?.find((m) => m.id === activeModeId);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------
	const onSelect = (modeId: string) => {
		const isDeselecting = modeId === defaultModeId;
		forge.updateFrame({
			update: {
				id: frame.id,
				metadata: {
					...frame.metadata,
					mode: isDeselecting ? null : modeId,
				},
			},
		});
	};

	const menuItems = useMemo(() => {
		if (!dsModes) return [];
		return dsModes.map(
			(m) => new MenuItemAction(m.label, '', () => onSelect(m.id), false, m.id === activeModeId)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dsModes, activeModeId]);

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------
	if (!dsModes || dsModes.length < 2 || !activeMode) return null;

	const swatchColor = activeMode.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT;
	const iconSize = compact ? 10 : 12;

	return (
		<DropdownMenu menuItems={menuItems}>
			<S.Trigger $compact={compact}>
				<S.Swatch $color={swatchColor} $compact={compact} />
				<S.Label>{activeMode.label}</S.Label>
				<Icon type={IconType.FILLED_ARROW_DOWN} width={iconSize} height={iconSize} />
			</S.Trigger>
		</DropdownMenu>
	);
};

export default FrameModeDropdown;
